import type React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  Rating,
} from "@mui/material";
import { Close as CloseIcon, Person as PersonIcon } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import ResizableTextarea from "../../../../components/common/ResizableTextArea";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";
import { User } from "../../../../store/slice/AuthSlice";
import { AxiosError } from "axios";
import type { IColorTokens, IThemeState } from "../../../../types/theme";

interface CrewMember {
  id: string | number;
  crew_member: {
    id: string | number;
    user: {
      first_name: string;
      last_name: string;
      email: string;
      profile_picture?: string | null;
      username?: string;
    };
  };

  role?: string;
}

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  crewMember: CrewMember | null;
  streamName?: string;
  theme: IThemeState;
  colors: IColorTokens;
  onReviewSubmitted?: () => void;
  streamId: number;
}

const ReviewModal: React.FC<ReviewModalProps> = ({
  open,
  onClose,
  crewMember,
  theme: _theme,
  colors,
  onReviewSubmitted,
  streamId,
}) => {
  void _theme;
  const [rating, setRating] = useState<number | null>(0);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const user = useSelector(
    (state: RootState) => state.auth.user
  ) as User | null;

  const handleSubmit = async () => {
    if (!user) {
      toast.error("You must be logged in to submit a review.");
      return;
    }

    setLoading(true);

    try {
      // Validation: rating must be provided
      if (!rating || rating === 0) {
        toast.error("Please provide a rating before proceeding.");
        return;
      }

      // Validation: user cannot rate themselves
      if (user.id === crewMember?.id) {
        toast.error("You cannot give a review to yourself.");
        return;
      }

      const payload = {
        crew_member_id: crewMember?.crew_member?.id,
        rating,
        comment: feedback,
        stream_id: streamId,
        crew_member: crewMember?.crew_member?.id,
        crew_member_detail: {
          user: {
            email: crewMember?.crew_member.user.email,
            username: crewMember?.crew_member.user.username,
          },
        },
        stream: streamId,
        rater: user.id,
        rater_detail: {
          first: user.username,
          username: user.username,
          company: {
            name: "Muzukuru",
          },
        },
      };

      await apiService.post("/api/crews/crew-ratings/", payload);

      toast.success("Thank you. Feedback received!", {
        position: "top-right",
        autoClose: 2000,
        theme: "light",
      });
      handleClose();
      if (onReviewSubmitted) {
        onReviewSubmitted();
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.message ||
            "Failed to create a review. Please try again.",
          { position: "top-right", autoClose: 2000, theme: "light" }
        );
      } else {
        console.error("Failed to create a review", error);
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setFeedback("");
    onClose();
  };

  if (!crewMember) return null;

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              m: 0,
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ textAlign: "center", py: 4 }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Avatar
                src={crewMember.crew_member.user.profile_picture ?? undefined}
                sx={{
                  width: 120,
                  height: 120,
                  margin: "0 auto 16px",
                }}
              >
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                {crewMember?.crew_member?.user?.first_name || ""}{" "}
                {crewMember?.crew_member?.user?.last_name || ""}
              </Typography>

              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{
                  mb: 3,
                  alignItems: "center",
                }}
              >
                {crewMember.role || "CREW MEMBER"}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Rating
                  value={rating}
                  onChange={(event, newValue) => {
                    setRating(newValue);
                  }}
                  size="large"
                  sx={{
                    fontSize: "2rem",
                    "& .MuiRating-iconEmpty": {
                      color: colors.primary[300],
                    },
                  }}
                />
              </Box>
              <ResizableTextarea
                label="Comment"
                value={feedback}
                onChange={(value) => setFeedback(value)}
                colors={colors}
                reduxTheme={reduxTheme}
              />
            </motion.div>
          </DialogContent>

          <DialogActions sx={{ gap: 2 }}>
            <Button onClick={handleClose} variant="outlined" disabled={loading}>
              Discard
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Add Pilot Review"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default ReviewModal;
